import { createSlice, createAsyncThunk, PayloadAction, AsyncThunkAction } from '@reduxjs/toolkit';
import axiosInstance from '../api';
import { CustomObject, Filter, FindFleetState, LevelAttribute, FindFleetNewQueryRequest, FindFleetFetchMembersRequest, FleetDataRequest, ZipRadiusRequest, FindFleetResult } from '../interfaces/findFleetInterfaces';
import { RootState } from './store';
import { Query, Result } from '../interfaces/queryBuilderInterfaces';
import { addFleetSearchToEnhancedFilterCriteria, convertInputToEnhancedFilterCriteria, convertZipFilterToEnhancedFilterCriteria } from '../utils/util';
interface MemberValue {
    caption: string;
    uniqueName: string;
    name: string;
}

interface Zip {
    zipCode: string;
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchAllFilterValues = (queryName: string) => async (dispatch: any, getState: any) => {
    const { levels } = getState().findFleet;
    const promises = levels.filter((level: LevelAttribute) => level.type === "FILTER").map(async (level: LevelAttribute) => {
        await delay(1000);
        return dispatch(fetchFilterValues({ queryName, level: level.uniqueName }));
    });
    await Promise.all(promises);
};

export const fetchFacts = createAsyncThunk<Result, CustomObject>('findFleet/fetchFacts', async (payload) => {
    const response = await axiosInstance.post(`/rest/fleet/detail/data`, payload);
    return response.data;
});
export const fetchFactsForDownload = createAsyncThunk<FindFleetResult, CustomObject>('findFleet/fetchFactsForDownload', async (payload) => {
    const response = await axiosInstance.post(`/rest/fleet/detail/data`, payload);
    return response.data;
});

export const getZipWithinRadius = createAsyncThunk<any, ZipRadiusRequest>('findFleet/zipRadius', async (payload) => {
    const response = await axiosInstance.get(`/rest/zip/${payload.zipCode}/radiate/${payload.radius}`);
    return response.data;
});

export const fetchAllFactsForDownload = createAsyncThunk<FindFleetResult[], CustomObject[]>(
    'findFleet/fetchAllFactsForDownload',
    async (params, { dispatch }) => {
        const results: FindFleetResult[] = [];
        const promises = params.map(async (param) => {
            try {
                const result = await dispatch(fetchFactsForDownload(param)).unwrap();
                // add fleet id in result
                result.fleetId = param.fleetId;
                results.push(result);
            } catch (error) {
                return [];
            }
        });

        await Promise.all(promises);
        return results;
    }
);

export const getZips = (state: { findFleet: FindFleetState }) => {
    const filters = state.findFleet.availableFilters;
    let zipFilterValues: MemberValue[] = [];
    for (const key in filters) {
        if (key.toUpperCase().includes("ZIP")) {
            if (Array.isArray(filters[key])) {
                zipFilterValues = filters[key] as MemberValue[];
            }
        }
    }
    return zipFilterValues;
};

export const getfilterModels = (state: { findFleet: FindFleetState }) => {
    return state.findFleet.filtered;
};

export const fetchLevels = createAsyncThunk('findFleet/fetchLevels',
    async () => {
        const response = await axiosInstance.get(`/rest/fleet/levels`);
        return response.data;
    }
);

export const fetchFilterValues = createAsyncThunk('findFleet/fetchFilterValues',
    async (payload: FindFleetFetchMembersRequest) => {
        const response = await axiosInstance.get(`/rest/olapquery/${payload.queryName}/members/${payload.level}`);
        return { level: payload.level, data: response.data };
    }
);

export const applyFilters = createAsyncThunk('findFleet/applyFilters',
    async (_, { getState }) => {
        const state = getState() as RootState;
        const filters: CustomObject = state.findFleet.filtered;
        const response = await axiosInstance.post(`/rest/findFleets`, filters);
        return response.data;
    }
);

// Initial state
const initialState: FindFleetState = {
    levels: [],
    fleetLevelIDs: [],
    fleetKeys: [],
    loading: false,
    levelLoading: false,
    applyLoading: false,
    newQueryLoading: false,
    resultMetaLoading: false,
    fetchFactsLoading: false,
    zipLoading: false,
    error: null,
    filtered: {},
    zipFilter: [],
    formatedFiltered: {},
    appliedFilter: {},
    availableFilters: {},
    selectedFleet: {},
    selectedFleets: [],
    selectedFleetToDisplay: {},
    selectedFleetsToDownload: null,
    selectedFleetsToDisplay: null,
    geoFilter: {
        zipCode: '',
        radius: 25
    },
    facts: null,
    factsForDownload: null,
    query: null,
    results: null,
    metaData: null,
    fleets: null,
    currentFleet: null,
    totalFleets: 0,
    searchFleetText: ''
};

const findFleetSlice = createSlice({
    name: 'findFleet',
    initialState,
    reducers: {
        updateSelectedFilter(state, action: PayloadAction<Partial<Filter>>) {
            state.filtered = {
                ...action.payload
            };
        },
        updateZipFilter(state, action: PayloadAction<any>) {
            const zips: string[] = Array.isArray(action.payload.zips) ? action.payload.zips.map((z: Zip) => z.zipCode ? z.zipCode : z) : [];
            state.zipFilter = zips;
        },
        updateAvailableFilters(state, action: PayloadAction<Partial<Filter>>) {
            state.availableFilters = {
                ...state.availableFilters,
                ...action.payload
            };
            Object.assign(state.availableFilters, action.payload);
        },
        selectFleet(state, action: PayloadAction<CustomObject>) {
            state.selectedFleet = { ...action.payload };
        },
        selectFleetToDisplay(state, action: PayloadAction<CustomObject>) {
            state.selectedFleetToDisplay = { ...action.payload };
        },
        selectFleetsToDownload(state, action: PayloadAction<any>) {
            state.selectedFleetsToDownload = action.payload;
        },
        selectFleetsToDisplay(state, action: PayloadAction<any>) {
            state.selectedFleetsToDisplay = action.payload;
        },
        resetFilters(state) {
            return initialState;
        },
        setFiltered(state, action: PayloadAction<any>) {
            state.appliedFilter = action.payload;
        },
        setGeoFilter(state, action: PayloadAction<any>) {
            state.geoFilter = action.payload;
        },
        setformatedFiltered(state, action: PayloadAction<any>) {
            state.formatedFiltered = action.payload;
        },
        setQuery(state, action: PayloadAction<any>) {
            state.query = action.payload;
        },
        setSearchFleetText(state, action: PayloadAction<string>) {
            state.searchFleetText = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLevels.pending, (state) => {
                state.levelLoading = true;
                state.error = null;
            })
            .addCase(fetchLevels.fulfilled, (state, action) => {
                state.levelLoading = false;
                state.levels = action.payload;
                state.fleetLevelIDs = action.payload.filter((item: { type: any; }) => item.type === "ROW" || item.type === "METRIC" || item.type === "KEY")
                    .map((item: { id: any; }) => item.id);
                state.fleetKeys = action.payload.filter((item: { type: any; }) => item.type === "KEY")
                    .map((item: { id: any; }) => item.id);
            })
            .addCase(fetchLevels.rejected, (state, action) => {
                state.levelLoading = false;
                state.error = action.error.message || 'Failed to fetch filterable values';
            })
            .addCase(fetchFilterValues.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFilterValues.fulfilled, (state, action) => {
                const { level, data } = action.payload as { level: string, data: any };
                state.availableFilters[level] = data;
                state.loading = false;
            })
            .addCase(fetchFilterValues.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch filter values';
            })
            .addCase(applyFilters.pending, (state) => {
                state.applyLoading = true;
                state.error = null;
            })
            .addCase(applyFilters.fulfilled, (state, action) => {
                state.applyLoading = false;
                state.results = action.payload;
            })
            .addCase(applyFilters.rejected, (state, action) => {
                state.applyLoading = false;
                state.error = action.error.message || 'Failed to apply filters';
            }).addCase(newQuery.pending, (state) => {
                state.newQueryLoading = true;
            })
            .addCase(newQuery.fulfilled, (state, action) => {
                state.newQueryLoading = false;
                state.query = action.payload;
            })
            .addCase(newQuery.rejected, (state, action) => {
                state.newQueryLoading = false;
                state.error = action.error.message || null;
            })
            .addCase(resultMetaQuery.pending, (state) => {
                state.resultMetaLoading = true;
            })
            .addCase(resultMetaQuery.fulfilled, (state, action) => {
                state.resultMetaLoading = false;
                state.metaData = action.payload;
            })
            .addCase(resultMetaQuery.rejected, (state, action) => {
                state.resultMetaLoading = false;
                state.error = action.error.message || null;
            })
            .addCase(fetchFacts.pending, (state) => {
                state.fetchFactsLoading = true;
            })
            .addCase(fetchFacts.fulfilled, (state, action) => {
                state.fetchFactsLoading = false;
                state.facts = action.payload;
            })
            .addCase(fetchFacts.rejected, (state, action) => {
                state.fetchFactsLoading = false;
                state.error = action.error.message || null;
            })
            .addCase(fetchAllFactsForDownload.pending, (state) => {
                state.fetchFactsLoading = true;
            })
            .addCase(fetchAllFactsForDownload.fulfilled, (state, action) => {
                state.fetchFactsLoading = false;
                state.factsForDownload = action.payload;
            })
            .addCase(fetchAllFactsForDownload.rejected, (state, action) => {
                state.fetchFactsLoading = false;
                state.error = action.error.message || null;
            })
            .addCase(fetchTotalRecords.fulfilled, (state, action) => {
                state.totalFleets = action.payload || null;
            }).addCase(getZipWithinRadius.pending, (state) => {
                state.zipLoading = true;
            }).addCase(getZipWithinRadius.fulfilled, (state, action) => {
                state.zipLoading = false;
            });

    },
});

export const resultQuery = createAsyncThunk<any, FleetDataRequest>('findFleet/result', async (payload: FleetDataRequest) => {
    const response = await axiosInstance.post(`/rest/fleet/data`, payload);
    return response.data;
});

export const fetchTotalRecords = createAsyncThunk<any, FleetDataRequest>('findFleet/totalRecords', async (payload: FleetDataRequest) => {
    const response = await axiosInstance.post(`/rest/fleet/totalFleets`, payload);
    return response.data;
});

export const resultMetaQuery = createAsyncThunk<Result, Query>('findFleet/meta', async (payload) => {
    let response = await axiosInstance.get(`/rest/olapquery/${payload.name}/result/flattened`);
    if (response.data.result.resultFill === 'TimedOut') {
        do {
            await delay(1000);
            response = await axiosInstance.get(`/rest/olapquery/result/checkstatus/${response.data.uid}`);
        } while (response.data.result.resultFill === 'TimedOut');
    }
    return response.data;
});

export const {
    updateSelectedFilter,
    updateAvailableFilters,
    resetFilters,
    setFiltered,
    selectFleet,
    selectFleetToDisplay,
    selectFleetsToDownload,
    selectFleetsToDisplay,
    setGeoFilter,
    updateZipFilter
} = findFleetSlice.actions;

export const setupQuery = createAsyncThunk<Query, Query>('findFleet/setup', async (payload: Query) => {
    const response = await axiosInstance.post(`/rest/olapquery/${payload.name}/setup`, payload);
    return response.data;
});

export const newQuery = createAsyncThunk<Query, FindFleetNewQueryRequest>(
    'findFleet/new',
    async (request) => {
        const response = await axiosInstance.post(`/rest/olapquery/new`, request.formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (response.data.name) {
            const payload = response.data;
            const setupResp = await axiosInstance.post(`/rest/olapquery/${payload.name}/setup`, payload);
            return setupResp.data;
        }
    });
export const queryFilter = () => async (dispatch: any, getState: any) => {
    const state = getState() as RootState;
    const query = state.findFleet.query;
    const levels = state.findFleet.levels
    const filter: CustomObject = state.findFleet.filtered;
    const availableFilters: CustomObject = state.findFleet.availableFilters;
    const { enhancedFilterCriteria, fleetData } = convertInputToEnhancedFilterCriteria(filter as unknown as Record<string, { values: string[]; filterType: string; }>, availableFilters as { [key: string]: Filter[] }, levels);
    convertZipFilterToEnhancedFilterCriteria(enhancedFilterCriteria, state.findFleet.zipFilter, levels, fleetData);
    addFleetSearchToEnhancedFilterCriteria(enhancedFilterCriteria, state.findFleet.searchFleetText, levels, fleetData);
    if (query) {
        const updatedQuery: Query = { ...query, name: query.name, enhancedFilterCriteria };
        dispatch(setQuery(updatedQuery));
        dispatch(setformatedFiltered(fleetData));
    }
};

export const getResults = (state: { findFleet: FindFleetState }) => {
    return state.findFleet.results;
};

export const getSelectedFilter = (state: { findFleet: FindFleetState }) => {
    return state.findFleet.filtered;
};

export const getAppliedFilter = (state: { findFleet: FindFleetState }) => {
    return state.findFleet.appliedFilter;
};

export const { setQuery, setformatedFiltered, setSearchFleetText } = findFleetSlice.actions;
export default findFleetSlice.reducer;
